/**
 * Playback synchronisation primitives (design §7.2). These are the seed of
 * sync spike 0.1a: a master clock and the drift-correction policy that keeps
 * every <video>/<audio> element slaved to it. Plain TypeScript, outside the
 * React render cycle.
 *
 * @module
 */

/** Below this drift (ms) do nothing. Half the ±40 ms target, because two streams can sit at opposite ends. */
export const DRIFT_IGNORE_MS = 20;
/** Above this drift (ms) hard-seek instead of nudging the rate. */
export const DRIFT_SEEK_MS = 500;
/** Fractional playbackRate change used to close small drifts. */
export const RATE_NUDGE = 0.05;

export type DriftAction =
  | { kind: "none"; playbackRate: number }
  | { kind: "nudge"; playbackRate: number }
  | { kind: "seek"; toSeconds: number };

/**
 * Decides how to correct one media element.
 * @param expectedSec where the element should be (T - track start), seconds
 * @param actualSec the element's currentTime, seconds
 * @param baseRate the timeline playback rate
 */
export function driftAction(
  expectedSec: number,
  actualSec: number,
  baseRate = 1,
  toleranceMs = DRIFT_IGNORE_MS,
): DriftAction {
  const driftMs = (actualSec - expectedSec) * 1000;
  const abs = Math.abs(driftMs);
  if (abs < toleranceMs) return { kind: "none", playbackRate: baseRate };
  if (abs <= DRIFT_SEEK_MS) {
    // Ahead: slow down. Behind: speed up.
    const factor = driftMs > 0 ? 1 - RATE_NUDGE : 1 + RATE_NUDGE;
    return { kind: "nudge", playbackRate: baseRate * factor };
  }
  return { kind: "seek", toSeconds: Math.max(0, expectedSec) };
}

/** Source of monotonic time in ms (performance.now, or AudioContext time). */
export type TimeSource = () => number;

/**
 * The master timeline clock. Position is a UTC instant in epoch ms.
 * When audio is active the controller passes an AudioContext-based source,
 * because the ear notices glitches more than the eye.
 */
export class PlaybackClock {
  private anchorUtcMs: number;
  private anchorSourceMs: number;
  private rate = 1;
  private playing = false;

  constructor(
    startUtcMs: number,
    private source: TimeSource = () => performance.now(),
  ) {
    this.anchorUtcMs = startUtcMs;
    this.anchorSourceMs = source();
  }

  /** Current timeline position, UTC epoch ms. */
  now(): number {
    if (!this.playing) return this.anchorUtcMs;
    return this.anchorUtcMs + (this.source() - this.anchorSourceMs) * this.rate;
  }

  get isPlaying(): boolean {
    return this.playing;
  }

  get playbackRate(): number {
    return this.rate;
  }

  play(): void {
    if (this.playing) return;
    this.anchorSourceMs = this.source();
    this.playing = true;
  }

  pause(): void {
    if (!this.playing) return;
    this.reanchor();
    this.playing = false;
  }

  seek(utcMs: number): void {
    this.anchorUtcMs = utcMs;
    this.anchorSourceMs = this.source();
  }

  setRate(rate: number): void {
    if (!(rate >= 0.25 && rate <= 8)) throw new RangeError(`rate ${rate} outside 0.25-8`);
    this.reanchor();
    this.rate = rate;
  }

  /** Switch time source (e.g. to AudioContext time) without a jump. */
  setSource(source: TimeSource): void {
    this.reanchor();
    this.source = source;
    this.anchorSourceMs = source();
  }

  private reanchor(): void {
    this.anchorUtcMs = this.now();
    this.anchorSourceMs = this.source();
  }
}

/** Longest interpolation past the last AudioContext.currentTime update, in ms. */
const MAX_AUDIO_INTERPOLATION_MS = 50;

/**
 * A smooth, monotonic time source driven by AudioContext.currentTime, which
 * advances in render-quantum steps. getOutputTimestamp() would interpolate
 * for us, but WebKit's performanceTime runs at twice wall-clock speed.
 */
export function audioContextTimeSource(
  ctx: { readonly currentTime: number },
  wall: TimeSource = () => performance.now(),
): TimeSource {
  let lastCtxMs = -1;
  let anchorWallMs = 0;
  let last = -Infinity;
  return () => {
    const ctxMs = ctx.currentTime * 1000;
    const w = wall();
    if (ctxMs !== lastCtxMs) {
      lastCtxMs = ctxMs;
      anchorWallMs = w;
    }
    last = Math.max(last, ctxMs + Math.min(w - anchorWallMs, MAX_AUDIO_INTERPOLATION_MS));
    return last;
  };
}

/**
 * Watches an AudioContext's clock against wall time while playing. A context
 * with no working output either never runs or crawls (seen with WebView2's
 * audio sandbox on a VM), and the playback clock it drives would too.
 */
export class AudioWatch {
  private last: { ctxS: number; wallMs: number } | null = null;
  private slow = 0;
  private ran = false;

  constructor(
    /** Consecutive slow checks before giving up on the context. */
    private readonly limit = 2,
    /** Below this share of wall-clock speed a check counts as slow. */
    private readonly minRate = 0.5,
  ) {}

  /** Call about once a second while playing. */
  check(ctxS: number, wallMs: number): "ok" | "never-ran" | "stopped" {
    const last = this.last;
    this.last = { ctxS, wallMs };
    if (!last || wallMs <= last.wallMs) return "ok";
    const rate = ((ctxS - last.ctxS) * 1000) / (wallMs - last.wallMs);
    if (rate >= this.minRate) {
      this.slow = 0;
      this.ran = true;
      return "ok";
    }
    this.slow += 1;
    if (this.slow < this.limit) return "ok";
    return this.ran ? "stopped" : "never-ran";
  }

  /** Paused: the next check starts afresh. */
  pause(): void {
    this.last = null;
    this.slow = 0;
  }
}
